import { classNames, isStandard } from '@library/utils';
import { DateTime } from 'luxon';
import { DiscountIndicator } from '@library/components/DiscountIndicator';
import type { FormattedNewWagerLineupDetails } from '@library/types/newWagers';
import { OddsTypeIndicator } from '@library/components/OddsTypeIndicator';
import { PLAYER_PLACEHOLDER_IMAGE_URL } from '@bo-constants/newWagers';
import { lineupPlayerStatusDisplay } from '@bo-utils/newWagers';
import moreArrowIcon from '@library/assets/images/icons/more-arrow.svg';
import { useMemo } from 'react';

export interface PlayerLineupRowProps {
  isModalView?: boolean;
  lineupRow: FormattedNewWagerLineupDetails;
}

export const PlayerLineupRow = ({
  isModalView = false,
  lineupRow
}: PlayerLineupRowProps) => {
  const {
    isPromo,
    leagueName,
    player,
    predictionStats,
    projectionStats,
    scoreStats,
    statType,
    wagerType
  } = lineupRow;

  const { lineScore, oddsType } = predictionStats;
  const { discountName, flashSaleLineScore, startTime } = projectionStats;
  const { score } = scoreStats;

  const {
    borderColor,
    label: statusLabel,
    statusBadge
  } = lineupPlayerStatusDisplay(lineupRow);

  const displayFinalScore = useMemo(
    () => !['in_progress', 'dnp', 'pre_game'].includes(player.status),
    [player.status]
  );

  const displayPosition = useMemo(
    () =>
      player.position.toLowerCase() !== 'player'
        ? `• ${player.position} •`
        : '•',
    [player.position]
  );

  const showOddsType = useMemo(() => !isStandard(oddsType), [oddsType]);
  const showPromo = useMemo(
    () => isPromo && discountName,
    [isPromo, discountName]
  );

  const startTimeFormat = useMemo(
    () => (isModalView ? 'M/d/yy h:mma' : 'M/d'),
    [isModalView]
  );
  const startTimeDisplay = useMemo(
    () => DateTime.fromISO(startTime).toFormat(startTimeFormat),
    [startTime, startTimeFormat]
  );

  const formattedStatType = useMemo(
    () => (isModalView ? statType.name : statType.name.replace('(Combo)', '')),
    [isModalView, statType.name]
  );

  const formattedLineScore = useMemo(
    () => (isPromo && flashSaleLineScore ? flashSaleLineScore : lineScore),
    [flashSaleLineScore, isPromo, lineScore]
  );

  return (
    <div
      className="flex h-16 items-center gap-4"
      data-testid="player-lineup-row"
    >
      {/* Player Image */}
      <div
        className={classNames(
          'from-soFresh-120 relative size-10 rounded-full bg-gradient-to-r to-[#2F3046] outline-2 outline-offset-0',
          borderColor
        )}
      >
        <img
          className="size-full rounded-full object-cover"
          src={player.imageUrl}
          alt={`${player.name} headshot`}
          onError={(e) => {
            e.currentTarget.src = PLAYER_PLACEHOLDER_IMAGE_URL;
          }}
          style={{ imageRendering: '-webkit-optimize-contrast' }}
        />
        {statusBadge && (
          <img
            className="absolute -right-0.5 bottom-[-2.5px] size-4"
            src={statusBadge}
            alt={player.name}
          />
        )}
      </div>

      {/* Player Info */}
      <div className="text-soClean-180 flex h-10 shrink-0 flex-col justify-around text-xs">
        <p className="w-full">
          <span className="text-soClean-100 text-[13px] font-semibold">
            {player.name}
          </span>
          <span className="ml-2">{leagueName}</span>
        </p>
        <p>
          <span className="pr-1">{player.team}</span>
          <span className="pr-1">{displayPosition}</span>
          <span className="lowercase">{startTimeDisplay}</span>
        </p>
      </div>

      {/* Player Stats/Results */}
      <div className="ml-auto flex items-center">
        <div className="flex flex-col">
          <div className="flex items-center justify-end pb-[3px]">
            {showOddsType && (
              <OddsTypeIndicator
                className="mr-2 size-3.5 rotate-12 items-center"
                oddsType={oddsType}
              />
            )}
            {showPromo && (
              <DiscountIndicator
                className="mr-2 rotate-12"
                discountName={discountName}
              />
            )}
            <p className="text-soClean-180 text-xs">
              <span className="text-soClean-100">{formattedLineScore}</span>
              <span className="ml-2 inline-block">{formattedStatType}</span>
            </p>
          </div>
          <p className="text-soClean-180 flex items-center justify-end text-xs">
            <span>{statusLabel}</span>
            {displayFinalScore && (
              <span className="text-soClean-100 ml-2">{score}</span>
            )}
          </p>
        </div>
        <img
          className={classNames(
            '-mr-1 ml-2 size-4',
            wagerType === 'under' ? 'rotate-180' : ''
          )}
          src={moreArrowIcon}
          alt="arrow down"
          onError={(e) => {
            e.currentTarget.src = moreArrowIcon;
          }}
        />
      </div>
    </div>
  );
};
